import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { API_URL } from '../../services/api';

const Footer: React.FC = () => {
    const [email, setEmail] = useState('');
    const [sending, setSending] = useState(false);
    const [feedback, setFeedback] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);

    const onSubscribe = async (e: React.FormEvent) => {
        e.preventDefault();
        setSending(true);
        setFeedback(null);
        try {
            const res = await fetch(`${API_URL}/newsletter/subscribe`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: email.trim(), source: 'footer' }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.error || 'Inscription impossible');
            setFeedback({ type: 'ok', text: data.message || 'Inscription confirmée.' });
            setEmail('');
        } catch (err: unknown) {
            setFeedback({
                type: 'err',
                text: err instanceof Error ? err.message : 'Erreur lors de l’inscription',
            });
        } finally {
            setSending(false);
        }
    };

    return (
        <footer className="bg-[#0a1b45] text-gray-300 pt-20 pb-10">
            <div className="container mx-auto px-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
                    <div>
                        <Link to="/" className="flex items-center space-x-2 mb-6">
                            <img src="/flashcv.png" alt="FlashCv Logo" className="h-8 w-auto object-contain" />
                            <span className="text-2xl font-bold text-white">FlashCv</span>
                        </Link>
                        <p className="mb-6 text-sm leading-relaxed text-gray-400">
                            FlashCv est votre partenaire carrière. Nous concevons des outils innovants pour
                            simplifier votre recherche d&apos;emploi et vous aider à décrocher le poste de
                            vos rêves.
                        </p>
                        <div className="space-y-3">
                            <div className="flex items-start space-x-3">
                                <div className="mt-1 bg-blue-900/50 p-2 rounded text-blue-400">
                                    <MapPin size={16} />
                                </div>
                                <span className="text-sm">Abidjan, Côte d&apos;Ivoire</span>
                            </div>
                            <div className="flex items-center space-x-3">
                                <div className="bg-blue-900/50 p-2 rounded text-blue-400">
                                    <Mail size={16} />
                                </div>
                                <span className="text-sm">asaphowou@gmail.com</span>
                            </div>
                            <div className="flex items-center space-x-3">
                                <div className="bg-blue-900/50 p-2 rounded text-blue-400">
                                    <Phone size={16} />
                                </div>
                                <span className="text-sm">+225 07 10 14 59 75</span>
                            </div>
                        </div>
                    </div>

                    <div>
                        <h3 className="text-white font-bold text-lg mb-6 relative inline-block">
                            Liens Rapides
                            <span className="absolute bottom-0 left-0 w-1/2 h-0.5 bg-blue-600"></span>
                        </h3>
                        <ul className="space-y-3 text-sm">
                            <li>
                                <a href="#about" className="hover:text-blue-400 transition-colors">
                                    À propos
                                </a>
                            </li>
                            <li>
                                <a href="#services" className="hover:text-blue-400 transition-colors">
                                    Nos Services
                                </a>
                            </li>
                            <li>
                                <a href="#modeles" className="hover:text-blue-400 transition-colors">
                                    Nos Modèles
                                </a>
                            </li>
                            <li>
                                <a href="#contact" className="hover:text-blue-400 transition-colors">
                                    Contact
                                </a>
                            </li>
                        </ul>
                    </div>

                    <div>
                        <h3 className="text-white font-bold text-lg mb-6 relative inline-block">
                            Solutions
                            <span className="absolute bottom-0 left-0 w-1/2 h-0.5 bg-blue-600"></span>
                        </h3>
                        <ul className="space-y-3 text-sm">
                            <li>
                                <Link to="/choix-modele" className="hover:text-blue-400 transition-colors">
                                    Création CV
                                </Link>
                            </li>
                            <li>
                                <Link to="/creer-lettre" className="hover:text-blue-400 transition-colors">
                                    Lettre de motivation
                                </Link>
                            </li>
                        </ul>
                    </div>

                    <div>
                        <h3 className="text-white font-bold text-lg mb-6 relative inline-block">
                            Newsletter
                            <span className="absolute bottom-0 left-0 w-1/2 h-0.5 bg-blue-600"></span>
                        </h3>
                        <p className="text-sm mb-6 text-gray-400">
                            Abonnez-vous pour recevoir nos derniers conseils et astuces carrière.
                        </p>
                        <form className="relative" onSubmit={onSubscribe}>
                            <input
                                type="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="Votre adresse e-mail"
                                disabled={sending}
                                className="w-full bg-blue-900/30 border border-blue-900/50 rounded-full py-3 px-5 pr-14 focus:outline-none focus:border-blue-500 text-white placeholder-gray-500 disabled:opacity-60"
                            />
                            <button
                                type="submit"
                                disabled={sending}
                                className="absolute right-1 top-1 bottom-1 bg-blue-600 w-10 h-10 rounded-full flex items-center justify-center text-white hover:bg-blue-700 transition-colors disabled:opacity-60"
                                aria-label="S’abonner à la newsletter"
                            >
                                {sending ? (
                                    <Loader2 size={16} className="animate-spin" />
                                ) : (
                                    <Send size={16} />
                                )}
                            </button>
                        </form>
                        {feedback && (
                            <p
                                className={`mt-3 text-xs ${
                                    feedback.type === 'ok' ? 'text-emerald-400' : 'text-red-400'
                                }`}
                            >
                                {feedback.text}
                            </p>
                        )}
                    </div>
                </div>

                <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center text-sm text-gray-500 gap-4">
                    <p>&copy; 2026 FlashCv. Tous droits réservés.</p>
                    <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
                        <Link to="#" className="hover:text-white">
                            Conditions Générales
                        </Link>
                        <Link to="#" className="hover:text-white">
                            Politique de Confidentialité
                        </Link>
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
